/**
 * Monthly closeout — the PURE rules behind the eight-month table on /admin/revenue-calculation.
 *
 * 🔴 WHY THE RULES LIVE HERE AND NOT IN THE COMPONENT. `jest.config.js` sets `transform: {}`
 * with `moduleFileExtensions: ['js', 'mjs', 'json']` — there is no jsx transform on this box, so
 * a rule written inside `MonthlyCloseoutTable.jsx` is a rule no test can import. Every decision
 * this table makes is exported from this file; the component only renders what it returns.
 *
 * 🔑 UNIT: every money figure that crosses this module is an integer number of CENTS, and the
 * one formatter that turns cents into text is declared below.
 * HISTORY (card 28334): `OrderDetailPage.jsx` used to declare its own `money()` helper that
 * guessed the magnitude (`n > 999 ? n / 100 : n`), rendering $1,325.36 correctly and $195.80 as
 * $195.80 in the same column. That guess is GONE — the page now imports `formatCentsUsd` from
 * `web/pages/admin/orderCostMath.js`, which reads cents unconditionally, the same rule as here.
 * The two formatters differ only in that this one adds thousands separators.
 *
 * 🔑 THE SOURCE IS THE RANGE-SCOPED SUMMARY ENDPOINT, ONE CALL PER MONTH. There is no monthly
 * endpoint: card 4938's eight windows live in `api/scripts/economics/*`, which is a script, not a
 * route. So the page asks `/admin/revenue-calculation/summary?dateFrom&dateTo` for each window and
 * this module maps one payload to one row.
 */

/** Rendered for any money or count cell whose true value is unknown. ⛔ NEVER $0.00. */
export const CLOSEOUT_UNRESOLVED_LABEL = 'n/a (unresolved)';

export const CLOSEOUT_YEAR = 2026;

/** January–August 2026 — the eight months card 4938 closed out. */
export const CLOSEOUT_MONTH_COUNT = 8;

const MONTH_NAMES = Object.freeze([
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]);

/**
 * PURE. The month windows, derived from UTC epoch arithmetic — ⛔ never from hand-written ISO
 * strings, which is how a month silently acquires a local-time offset.
 *
 * 🔑 `dateTo` is the FIRST INSTANT OF THE NEXT MONTH and the endpoint's upper bound is
 * EXCLUSIVE — it publishes `range.upperBoundIsExclusive: true`. Using the last day of the month
 * would drop every order placed on it.
 */
export const buildCloseoutWindows = (year = CLOSEOUT_YEAR, monthCount = CLOSEOUT_MONTH_COUNT) =>
  Array.from({ length: monthCount }, (_unused, index) =>
    Object.freeze({
      key: `${year}-${String(index + 1).padStart(2, '0')}`,
      label: `${MONTH_NAMES[index]} ${year}`,
      dateFrom: new Date(Date.UTC(year, index, 1)).toISOString(),
      dateTo: new Date(Date.UTC(year, index + 1, 1)).toISOString()
    }));

/** PURE. A finite number, or null. Anything else — undefined, NaN, a string — is unknown. */
const toFiniteOrNull = (value) => {
  const parsed = Number(value);
  return value === null || value === undefined || !Number.isFinite(parsed) ? null : parsed;
};

/**
 * PURE. Integer CENTS → display text. A null renders as the unresolved label, ⛔ never as zero;
 * a real zero renders as $0.00, because a measured zero is an answer.
 */
export const formatCloseoutMoney = (cents) => {
  const value = toFiniteOrNull(cents);
  if (value === null) return CLOSEOUT_UNRESOLVED_LABEL;
  const dollars = Math.abs(value) / 100;
  const text = dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${value < 0 ? '-' : ''}$${text}`;
};

/** PURE. A count → display text. Same rule: unknown is named, ⛔ never rendered as 0. */
export const formatCloseoutCount = (count) => {
  const value = toFiniteOrNull(count);
  return value === null ? CLOSEOUT_UNRESOLVED_LABEL : String(value);
};

/* ---------------------------------------------------------------- readers (one key each) */

/**
 * 🚨 THE ORDERS COLUMN IS `population.orderCount`, AND THE CHOICE IS LOAD-BEARING.
 * One `/summary` payload publishes THREE different order counts, measured live for July 2026:
 *   · `storedEconomics.population.orderCount` = 20 — the canonical reporting population
 *   · `storedEconomics.orderCount`            = 16 — the same minus the unresolved zero-revenue
 *                                                    rows `aggregateEconomics` drops
 *   · `orders.orderCount`                     = 38 — a status-filtered catalog population the
 *                                                    page itself warns must not be read against
 *                                                    the stored-economics figures
 * Card 4938's published artifact reports July as 20, so this is the one that agrees with it.
 */
export const readOrderCount = (summary) =>
  toFiniteOrNull(summary?.storedEconomics?.population?.orderCount);

/** The population's own sentence, rendered as the table caption so the count cannot be misread. */
export const readPopulationBasis = (summary) =>
  summary?.storedEconomics?.population?.basis ?? null;

/** Rows whose profit is unresolved. Absent means none reported, which is 0 — not unknown. */
export const readUnresolvedCount = (summary) =>
  toFiniteOrNull(summary?.storedEconomics?.unresolvedLines) ?? 0;

/** Collected-basis gross. Published by card 4936; absent on an API older than that deploy. */
export const readCollectedGrossCents = (summary) =>
  toFiniteOrNull(summary?.storedEconomics?.collected?.grossCents);

export const readUncollectedCount = (summary) =>
  toFiniteOrNull(summary?.storedEconomics?.uncollected?.orderCount);

export const readUncollectedGrossCents = (summary) =>
  toFiniteOrNull(summary?.storedEconomics?.uncollected?.grossCents);

export const readTotalExpenseCents = (summary) =>
  toFiniteOrNull(summary?.storedEconomics?.totalExpense?.value);

export const readProfitCents = (summary) =>
  toFiniteOrNull(summary?.storedEconomics?.profit?.value);

/**
 * The row-set's worst-member quality label, computed by the API itself
 * (`buildStoredEconomicsKpis`: one estimated line makes the whole set estimated). ⛔ Not
 * re-derived here — a second implementation of how good is this data is how two surfaces
 * start disagreeing about the same rows.
 */
export const readSourceQualityLabel = (summary) =>
  summary?.storedEconomics?.grossRevenue?.dataQuality ?? null;

/** The counts behind that label, in card 4938's verified / estimated / unresolved order. */
export const readSourceQualityCounts = (summary) =>
  Object.freeze({
    verified: toFiniteOrNull(summary?.storedEconomics?.dataQuality?.verified) ?? 0,
    estimated: toFiniteOrNull(summary?.storedEconomics?.dataQuality?.estimated) ?? 0,
    unresolved: readUnresolvedCount(summary)
  });

/**
 * 🚨🔴 THE RULE THIS CARD TURNS ON. A month carrying ANY unresolved line has an UNKNOWN expense,
 * and therefore an unknown profit — even though the endpoint publishes a NUMBER for both.
 *
 * Measured live, February 2026: `totalExpense.value` = 0 and `profit.value` = 119900 with
 * `unresolvedLines` = 1, because `aggregateEconomics` sums a null cost line as zero and counts it
 * separately. Rendered literally that is $0.00 expense, $1,199.00 profit for a month whose
 * medication cost nobody knows. Card 4938's artifact publishes both months as unresolved, so
 * taking the endpoint's number at face value would have put two Oval surfaces in contradiction.
 *
 * ⛔ Gross is NOT suppressed: `gross_revenue` is stored per row and is known regardless.
 */
export const isMonthBlocked = (summary) => readUnresolvedCount(summary) > 0;

/** PURE. One month window plus its `/summary` payload → one rendered row. */
export const toCloseoutRow = (window, summary) => {
  const blocked = isMonthBlocked(summary);
  return Object.freeze({
    key: window?.key ?? null,
    label: window?.label ?? null,
    orderCount: readOrderCount(summary),
    collectedGrossCents: readCollectedGrossCents(summary),
    uncollectedCount: readUncollectedCount(summary),
    uncollectedGrossCents: readUncollectedGrossCents(summary),
    unresolvedCount: readUnresolvedCount(summary),
    // Blocked by an unresolved line ⇒ null. ⛔ Never the endpoint's number, which absorbed it as 0.
    totalExpenseCents: blocked ? null : readTotalExpenseCents(summary),
    profitCents: blocked ? null : readProfitCents(summary),
    sourceQualityLabel: readSourceQualityLabel(summary),
    sourceQualityCounts: readSourceQualityCounts(summary),
    populationBasis: readPopulationBasis(summary),
    isBlocked: blocked
  });
};

/**
 * PURE. Every window gets a row, in window order, whether or not its fetch produced a payload —
 * a month that failed to load renders as unresolved rather than vanishing from the closeout.
 */
export const buildCloseoutRows = (windows = [], summariesByKey = {}) =>
  (Array.isArray(windows) ? windows : []).map((window) =>
    toCloseoutRow(window, summariesByKey?.[window?.key] ?? null));

/**
 * PURE. The positional form the page uses: `Promise.all` hands back payloads in window order,
 * so zipping them by index is the whole mapping. It lives here rather than in the page for the
 * reason at the top of this file — a rule inside a `.jsx` is a rule no test can import.
 *
 * A payload may be `null` (that month's request was rejected and swallowed so the other seven
 * still render); `toCloseoutRow` turns a null into a fully unresolved row, ⛔ never a zero one.
 */
export const buildCloseoutRowsFromPayloads = (windows = [], payloads = []) =>
  (Array.isArray(windows) ? windows : []).map((window, index) =>
    toCloseoutRow(window, Array.isArray(payloads) ? payloads[index] : null));

/* ------------------------------------------------- the one effect composition in this file */

/**
 * The eight windows, fetched. `getSummary` is INJECTED rather than imported, so this function
 * performs no I/O of its own: the page hands it `revenueCalculationApi.getSummary` and a test
 * hands it a fake. That is what makes the rejected-month branch below testable at all — an
 * error path is untested until something actually makes it fire.
 *
 * 🔑 ONE CALL PER MONTH, because there is no monthly endpoint: card 4938's eight windows live in
 * `api/scripts/economics/*`, which is a script and not a route. Measured on the live box:
 * 1.43 s for a single window, 7.9 s wall for all eight in parallel.
 *
 * 🔑 A REJECTED MONTH RESOLVES TO `null`, NOT TO A REJECTION — one failed window must not take
 * the other seven down with it, and `toCloseoutRow` renders a null payload as a fully
 * unresolved row. ⛔ Never a zero one: "we could not read March" and "March earned nothing" are
 * different statements and this table is required to tell them apart.
 */
export const fetchCloseoutRows = async (getSummary, windows = buildCloseoutWindows()) =>
  buildCloseoutRowsFromPayloads(
    windows,
    await Promise.all(windows.map(({ dateFrom, dateTo }) =>
      getSummary({ dateFrom, dateTo }).catch(() => null)))
  );

export default {
  CLOSEOUT_UNRESOLVED_LABEL,
  CLOSEOUT_YEAR,
  CLOSEOUT_MONTH_COUNT,
  buildCloseoutWindows,
  formatCloseoutMoney,
  formatCloseoutCount,
  readOrderCount,
  readPopulationBasis,
  readUnresolvedCount,
  readCollectedGrossCents,
  readUncollectedCount,
  readUncollectedGrossCents,
  readTotalExpenseCents,
  readProfitCents,
  readSourceQualityLabel,
  readSourceQualityCounts,
  isMonthBlocked,
  toCloseoutRow,
  buildCloseoutRows,
  buildCloseoutRowsFromPayloads,
  fetchCloseoutRows
};
